// Service Worker Registration and Management

interface ServiceWorkerConfig {
  onUpdate?: (registration: ServiceWorkerRegistration) => void
  onSuccess?: (registration: ServiceWorkerRegistration) => void
  onError?: (error: Error) => void
  onOffline?: () => void
  onOnline?: () => void
}

class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null
  private updateAvailable = false
  private config: ServiceWorkerConfig = {}

  constructor(config: ServiceWorkerConfig = {}) {
    this.config = config
    this.setupEventListeners()
  }

  private setupEventListeners() {
    // Network status listeners
    window.addEventListener('online', () => {
      console.log('[ServiceWorker] Back online')
      this.config.onOnline?.()
      this.syncOfflineData()
    })

    window.addEventListener('offline', () => {
      console.log('[ServiceWorker] Gone offline')
      this.config.onOffline?.()
    })

    // Listen for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[ServiceWorker] Controller changed')
        if (this.updateAvailable) {
          window.location.reload()
        }
      })
    }
  }

  async register(swUrl: string = '/service-worker.js'): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      console.log('[ServiceWorker] Not supported')
      return null
    }

    try {
      const registration = await navigator.serviceWorker.register(swUrl, {
        scope: '/'
      })

      this.registration = registration
      console.log('[ServiceWorker] Registered successfully')

      // Check for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing

        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content available
              this.updateAvailable = true
              this.config.onUpdate?.(registration)
            } else if (newWorker.state === 'activated' && !navigator.serviceWorker.controller) {
              // First install
              this.config.onSuccess?.(registration)
            }
          })
        }
      })

      // Check for updates periodically
      this.startUpdateCheck()

      return registration
    } catch (error) {
      console.error('[ServiceWorker] Registration failed:', error)
      this.config.onError?.(error as Error)
      return null
    }
  }

  async unregister(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      
      for (const registration of registrations) {
        await registration.unregister()
      }

      this.registration = null
      console.log('[ServiceWorker] Unregistered successfully')
      return true
    } catch (error) {
      console.error('[ServiceWorker] Unregistration failed:', error)
      return false
    }
  }

  async update(): Promise<void> {
    if (!this.registration) {
      console.warn('[ServiceWorker] No registration to update')
      return
    }

    try {
      await this.registration.update()
      console.log('[ServiceWorker] Update check completed')
    } catch (error) {
      console.error('[ServiceWorker] Update check failed:', error)
    }
  }

  skipWaiting(): void {
    if (!this.registration?.waiting) {
      console.warn('[ServiceWorker] No waiting worker to skip')
      return
    }

    this.registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    // Reset the update available state since we've triggered the update
    this.updateAvailable = false
  }

  resetUpdateState(): void {
    this.updateAvailable = false
    console.log('[ServiceWorker] Update state reset')
  }

  async forceUpdate(): Promise<void> {
    // Clear all caches to force fresh content
    await this.clearCache()
    // Unregister all service workers
    await this.unregister()
    // Hard reload the page
    window.location.reload()
  }

  async clearCache(): Promise<void> {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      console.warn('[ServiceWorker] Cannot clear cache - no controller')
      return
    }

    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' })
  }

  async cacheUrls(urls: string[]): Promise<void> {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      console.warn('[ServiceWorker] Cannot cache URLs - no controller')
      return
    }

    navigator.serviceWorker.controller.postMessage({ 
      type: 'CACHE_URLS',
      urls 
    })
  }

  async syncOfflineData(): Promise<void> {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      console.warn('[ServiceWorker] Background sync not supported')
      return
    }

    if (!this.registration) {
      console.warn('[ServiceWorker] No registration for sync')
      return
    }

    try {
      // Use message passing instead of direct sync API
      navigator.serviceWorker.controller.postMessage({ type: 'SYNC_DATA' })
      console.log('[ServiceWorker] Sync requested')
    } catch (error) {
      console.error('[ServiceWorker] Sync request failed:', error)
    }
  }

  private startUpdateCheck(): void {
    // Check for updates every 30 minutes
    setInterval(() => {
      this.update()
    }, 30 * 60 * 1000)
  }

  // Utility methods
  isOnline(): boolean {
    return navigator.onLine
  }

  isUpdateAvailable(): boolean {
    return this.updateAvailable
  }

  getRegistration(): ServiceWorkerRegistration | null {
    return this.registration
  }
}

// Singleton instance
let serviceWorkerManager: ServiceWorkerManager | null = null

export function initializeServiceWorker(config: ServiceWorkerConfig = {}): ServiceWorkerManager {
  if (!serviceWorkerManager) {
    serviceWorkerManager = new ServiceWorkerManager(config)
  }
  return serviceWorkerManager
}

export function getServiceWorkerManager(): ServiceWorkerManager | null {
  return serviceWorkerManager
}

// React hook for service worker
import { useEffect, useState } from 'react'

interface ServiceWorkerState {
  isOnline: boolean
  isUpdateAvailable: boolean
  registration: ServiceWorkerRegistration | null
}

export function useServiceWorker(config: ServiceWorkerConfig = {}) {
  const [state, setState] = useState<ServiceWorkerState>({
    isOnline: navigator.onLine,
    isUpdateAvailable: false,
    registration: null
  })

  useEffect(() => {
    const manager = initializeServiceWorker({
      ...config,
      onUpdate: (registration) => {
        setState(prev => ({ ...prev, isUpdateAvailable: true }))
        config.onUpdate?.(registration)
      },
      onSuccess: (registration) => {
        setState(prev => ({ ...prev, registration }))
        config.onSuccess?.(registration)
      },
      onOffline: () => {
        setState(prev => ({ ...prev, isOnline: false }))
        config.onOffline?.()
      },
      onOnline: () => {
        setState(prev => ({ ...prev, isOnline: true }))
        config.onOnline?.()
      }
    })

    // Register service worker
    manager.register().then(registration => {
      if (registration) {
        setState(prev => ({ ...prev, registration }))
      }
    })

    return () => {
      // Cleanup if needed
    }
  }, [])

  const updateServiceWorker = () => {
    const manager = getServiceWorkerManager()
    if (manager) {
      manager.skipWaiting()
    }
  }

  const resetUpdateState = () => {
    const manager = getServiceWorkerManager()
    if (manager) {
      manager.resetUpdateState()
    }
  }

  const clearCache = async () => {
    const manager = getServiceWorkerManager()
    if (manager) {
      await manager.clearCache()
    }
  }

  const cacheUrls = async (urls: string[]) => {
    const manager = getServiceWorkerManager()
    if (manager) {
      await manager.cacheUrls(urls)
    }
  }

  const forceUpdate = async () => {
    const manager = getServiceWorkerManager()
    if (manager) {
      await manager.forceUpdate()
    }
  }

  return {
    ...state,
    updateServiceWorker,
    resetUpdateState,
    clearCache,
    cacheUrls,
    forceUpdate
  }
}

// Offline queue for data persistence
interface QueuedRequest {
  id: string
  url: string
  method: string
  headers: Record<string, string>
  body?: string
  timestamp: number
}

class OfflineQueue {
  private dbName = 'staychecked-offline'
  private storeName = 'requests'
  private db: IDBDatabase | null = null

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id' })
        }
      }
    })
  }

  async addRequest(request: Omit<QueuedRequest, 'id' | 'timestamp'>): Promise<void> {
    if (!this.db) await this.init()

    const queuedRequest: QueuedRequest = {
      ...request,
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.add(queuedRequest)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getRequests(): Promise<QueuedRequest[]> {
    if (!this.db) await this.init()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async removeRequest(id: string): Promise<void> {
    if (!this.db) await this.init()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.delete(id)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clear(): Promise<void> {
    if (!this.db) await this.init()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }
}

export const offlineQueue = new OfflineQueue()

// Utility to check if app can work offline
export function canWorkOffline(): boolean {
  return 'serviceWorker' in navigator && 
         'caches' in window && 
         'indexedDB' in window
}

export default ServiceWorkerManager